import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {ChevronDownIcon, PlusIcon, TrashIcon} from "@heroicons/react/16/solid";
import {notify} from "@/components/Notifications";
import {getErrMsg} from "@/lib/errors";
import {
    DayOfWeek,
    Program,
    ProgramOverride,
    ProgramSchedule,
    useAddOverride,
    useAddSchedule,
    useDeleteOverride,
    useDeleteSchedule,
    useSaveProgram,
    useUpdateOverride,
    useUpdateSchedule,
} from "@/hooks/usePrograms";


type Props = {
    open: boolean;
    initial?: Partial<Program> | null;
    onClose: () => void;
};

function pickExistingImage(a?: unknown): { id?: string; url?: string } {
    const x = a as any;
    if (!x) return {};
    if (x.imageUrl) return {id: x?.id, url: x.imageUrl};
    return {};
}

const DAY_OPTIONS: DayOfWeek[] = [
    "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY",
];

export default function ProgramUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    // Core fields
    const [title, setTitle] = useState(initial?.title ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [producer, setProducer] = useState(initial?.producer ?? "");

    // File handling
    const [file, setFile] = useState<File | null>(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [touched, setTouched] = useState(false);

    // Existing media / deletion
    const [removedExisting, setRemovedExisting] = useState(false);
    const existing = useMemo(() => pickExistingImage(initial), [initial]);
    const existingImageUrl = removedExisting ? undefined : existing.url;

    // Preview
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    // Upload constraints
    const MAX_MB = 10;
    const ALLOWED_TYPES = useMemo(
        () => new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]),
        []
    );
    const acceptAttr = useMemo(() => Array.from(ALLOWED_TYPES).join(","), [ALLOWED_TYPES]);

    // Schedules & Overrides (editable lists)
    type ScheduleEdit = Omit<ProgramSchedule, "id"> & { id?: string };
    type OverrideEdit = Omit<ProgramOverride, "id"> & { id?: string };

    const [schedules, setSchedules] = useState<ScheduleEdit[]>(
        (initial?.schedules as ProgramSchedule[] | undefined)?.map(s => ({
            id: (s as any).id,
            dayOfWeek: s.dayOfWeek,
            startTime: s.startTime,
            endTime: s.endTime,
            producerOverride: s.producerOverride ?? null,
        })) ?? []
    );

    const [overrides, setOverrides] = useState<OverrideEdit[]>(
        (initial?.overrides as ProgramOverride[] | undefined)?.map(o => ({
            id: (o as any).id,
            date: o.date,
            startTime: o.startTime,
            endTime: o.endTime,
            producerOverride: o.producerOverride ?? null,
            titleOverride: o.titleOverride ?? null,
            descriptionOverride: o.descriptionOverride ?? null,
            imageUrl: (o as any).imageUrl ?? null,
            file: null,
            previewUrlOverride: null,
        })) ?? []
    );

    useEffect(() => {
        return () => {
            overrides.forEach(o => {
                if (o.previewUrlOverride) URL.revokeObjectURL(o.previewUrlOverride);
            });
        };
    }, [overrides]);

    const {mutateAsync: saveMeta, isPending} = useSaveProgram();
    const {mutateAsync: addSchedule} = useAddSchedule();
    const {mutateAsync: updateScheduleApi} = useUpdateSchedule();
    const {mutateAsync: deleteScheduleApi} = useDeleteSchedule();
    const {mutateAsync: addOverride} = useAddOverride();
    const {mutateAsync: updateOverrideApi} = useUpdateOverride();
    const {mutateAsync: deleteOverrideApi} = useDeleteOverride();

    function eqSchedule(a: any, b: any) {
        return a.dayOfWeek === b.dayOfWeek
            && a.startTime === b.startTime
            && a.endTime === b.endTime
            && (a.producerOverride ?? null) === (b.producerOverride ?? null);
    }

    function eqOverride(a: any, b: any) {
        return a.date === b.date
            && a.startTime === b.startTime
            && a.endTime === b.endTime
            && (a.producerOverride ?? null) === (b.producerOverride ?? null)
            && (a.titleOverride ?? null) === (b.titleOverride ?? null)
            && (a.descriptionOverride ?? null) === (b.descriptionOverride ?? null)
            && (a.imageUrl ?? null) === (b.imageUrl ?? null);
    }

    const initialSchedulesRef = useRef<ScheduleEdit[]>([]);
    const initialOverridesRef = useRef<OverrideEdit[]>([]);

    useEffect(() => {
        if (open) {
            setTitle(initial?.title ?? "");
            setDescription(initial?.description ?? "");
            setProducer(initial?.producer ?? "");
            setFile(null);
            setUploadPct(0);
            setRemovedExisting(false);
            setTouched(false);

            const initScheds = ((initial?.schedules as ProgramSchedule[] | undefined) ?? []).map(s => ({
                id: (s as any).id, dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime,
                producerOverride: s.producerOverride ?? null,
            }));
            const initOvers = ((initial?.overrides as ProgramOverride[] | undefined) ?? []).map(o => ({
                id: (o as any).id, date: o.date, startTime: o.startTime, endTime: o.endTime,
                producerOverride: o.producerOverride ?? null,
                titleOverride: o.titleOverride ?? null,
                descriptionOverride: o.descriptionOverride ?? null,
                imageUrl: (o as any).imageUrl ?? null,
            }));

            setSchedules(initScheds);
            setOverrides(initOvers);

            initialSchedulesRef.current = initScheds;
            initialOverridesRef.current = initOvers;
        }
    }, [open, initial]);

    const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (!ALLOWED_TYPES.has(f.type)) {
                notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
                return;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                notify.error(`File too large. Max size is ${MAX_MB}MB.`);
                return;
            }
            setFile(f);
        },
        [ALLOWED_TYPES]
    );

    const onPickOverrideFile = useCallback((idx: number, f?: File | null) => {
        setOverrides(prev => prev.map((o, i) => {
            if (i !== idx) return o;
            // revoke old preview
            if (o.previewUrlOverride) URL.revokeObjectURL(o.previewUrlOverride);

            if (!f) {
                // clear selection but keep server image url
                return {...o, file: null, previewUrlOverride: null};
            }
            if (!ALLOWED_TYPES.has(f.type)) {
                notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
                return o;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                notify.error(`File too large. Max size is ${MAX_MB}MB.`);
                return o;
            }
            const url = URL.createObjectURL(f);
            return {...o, file: f, previewUrlOverride: url};
        }));
    }, [ALLOWED_TYPES]);


    // ────────────────────────────────────────────────────────────────────────────
    // Validation
    // ────────────────────────────────────────────────────────────────────────────
    const errors = useMemo(() => {
        const e: Record<string, string> = {};

        if (!title.trim()) e.title = "Title is required.";
        if (!description.trim()) e.description = "Description is required.";
        if (!producer.trim()) e.producer = "Producer is required.";

        const needsFile = !isEdit || removedExisting;
        if (needsFile && !file && !existingImageUrl) e.file = "Please choose an image file.";

        // Validate schedule times
        schedules.forEach((s, idx) => {
            if (!s.dayOfWeek) e[`schedules.${idx}.dayOfWeek`] = "Day is required.";
            if (!s.startTime) e[`schedules.${idx}.startTime`] = "Start time is required.";
            if (!s.endTime) e[`schedules.${idx}.endTime`] = "End time is required.";
            if (s.startTime && s.endTime && s.endTime <= s.startTime) {
                e[`schedules.${idx}.range`] = "End time must be after start time.";
            }
        });

        // Validate overrides times
        overrides.forEach((o, idx) => {
            if (!o.date) e[`overrides.${idx}.date`] = "Date is required.";
            if (!o.startTime) e[`overrides.${idx}.startTime`] = "Start time is required.";
            if (!o.endTime) e[`overrides.${idx}.endTime`] = "End time is required.";
            if (o.startTime && o.endTime && o.endTime <= o.startTime) {
                e[`overrides.${idx}.range`] = "End time must be after start time.";
            }
        });

        return e;
    }, [title, description, producer, isEdit, removedExisting, file, existingImageUrl, schedules, overrides]);

    // ────────────────────────────────────────────────────────────────────────────
    // List helpers (Schedules / Overrides)
    // ────────────────────────────────────────────────────────────────────────────

    const updateSchedule = (idx: number, patch: Partial<ScheduleEdit>) =>
        setSchedules((prev) => prev.map((s, i) => (i === idx ? {...s, ...patch} : s)));

    const removeSchedule = (idx: number) =>
        setSchedules((prev) => prev.filter((_, i) => i !== idx));

    const addScheduleToList = () =>
        setSchedules((prev) => [
            ...prev,
            {dayOfWeek: "MONDAY", startTime: "06:00", endTime: "09:00", producerOverride: null},
        ]);

    const updateOverride = (idx: number, patch: Partial<OverrideEdit>) =>
        setOverrides((prev) => prev.map((o, i) => (i === idx ? {...o, ...patch} : o)));

    const removeOverride = (idx: number) =>
        setOverrides((prev) => prev.filter((_, i) => i !== idx));

    const addOverrideToList = () =>
        setOverrides((prev) => [
            ...prev,
            {
                date: (new Date().getDate().toString()),
                startTime: "09:00",
                endTime: "12:00",
                producerOverride: null,
                titleOverride: null,
                descriptionOverride: null,
                file: null
            },
        ]);

    // ────────────────────────────────────────────────────────────────────────────
    // Submit
    // ────────────────────────────────────────────────────────────────────────────
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            // 1) Save program meta (no schedules/overrides here)
            const metaResult = await saveMeta({
                id: initial?.id,
                title,
                description,
                producer,
                file,
                onUploadProgress: setUploadPct,
            });

            const programId = (initial?.id ?? metaResult?.id) as string;

            // 2) Compute diffs
            const initialSchedules = initialSchedulesRef.current;
            const initialOverrides = initialOverridesRef.current;

            const initialScheduleById = new Map(initialSchedules.filter(s => s.id).map(s => [s.id!, s]));
            const currentScheduleById = new Map(schedules.filter(s => s.id).map(s => [s.id!, s]));

            const initialOverrideById = new Map(initialOverrides.filter(o => o.id).map(o => [o.id!, o]));
            const currentOverrideById = new Map(overrides.filter(o => o.id).map(o => [o.id!, o]));

            // Schedules
            const schedulesToCreate = schedules.filter(s => !s.id);
            const schedulesToDelete = initialSchedules.filter(s => s.id && !currentScheduleById.has(s.id!));
            const schedulesToUpdate = schedules.filter(s => {
                if (!s.id) return false;
                const prev = initialScheduleById.get(s.id)!;
                return !eqSchedule(prev, s);
            });

            // Overrides
            const clean = (o: OverrideEdit) => o.date && o.startTime && o.endTime;

            const overridesToCreate = overrides.filter(o => !o.id && clean(o));
            const overridesToUpdate = overrides.filter(o => o.id && clean(o) && !eqOverride(initialOverrideById.get(o.id!)!, o));
            const overridesToDelete = initialOverrides.filter(o => o.id && !currentOverrideById.has(o.id!));

            // 3) Apply changes: deletes → updates → creates
            await Promise.all([
                ...schedulesToDelete.map(s => deleteScheduleApi(s.id!)),
                ...overridesToDelete.map(o => deleteOverrideApi(o.id!)),
            ]);

            await Promise.all([
                ...schedulesToUpdate.map(s => updateScheduleApi({
                    scheduleId: s.id!, dto: {
                        dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime,
                        producerOverride: s.producerOverride ?? null,
                    }
                })),
                ...overridesToUpdate.map(o => updateOverrideApi({
                    overrideId: o.id!,
                    dto: {
                        date: o.date, startTime: o.startTime, endTime: o.endTime,
                        producerOverride: o.producerOverride ?? null,
                        titleOverride: o.titleOverride ?? null,
                        descriptionOverride: o.descriptionOverride ?? null,
                    },
                    file: o.file ?? null,
                })),
            ]);

            await Promise.all([
                ...schedulesToCreate.map(s => addSchedule({
                    programId, dto: {
                        dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime,
                        producerOverride: s.producerOverride ?? null,
                    }
                })),
                ...overridesToCreate.map(o => addOverride({
                    programId,
                    dto: {
                        date: o.date, startTime: o.startTime, endTime: o.endTime,
                        producerOverride: o.producerOverride ?? null,
                        titleOverride: o.titleOverride ?? null,
                        descriptionOverride: o.descriptionOverride ?? null,
                    },
                    file: o.file ?? null,
                })),
            ]);

            setUploadPct(0);
            notify.success(`Program ${isEdit ? "updated" : "created"} successfully.`);
            onClose();
        } catch (err) {
            notify.error(getErrMsg(err, `Failed to ${isEdit ? "update" : "create"} program. Please try again.`));
        }
    };


    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-2xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Program" : "Add Program"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                {/* Title */}
                                <div>
                                    <label htmlFor="title"
                                           className="block text-sm font-medium text-blue-900">Title</label>
                                    <input
                                        id="title"
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title &&
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
                                </div>

                                {/* Description */}
                                <div>
                                    <label htmlFor="description"
                                           className="block text-sm font-medium text-blue-900">Description</label>
                                    <textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.description &&
                                        <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
                                </div>

                                {/* Producer */}
                                <div>
                                    <label htmlFor="producer" className="block text-sm font-medium text-blue-900">Default
                                        Producer</label>
                                    <input
                                        id="producer"
                                        type="text"
                                        value={producer}
                                        onChange={(e) => setProducer(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.producer}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.producer &&
                                        <p className="mt-1 text-xs text-red-600">{errors.producer}</p>}
                                </div>

                                {/* Image */}
                                <div>
                                    <label htmlFor="file" className="block text-sm font-medium text-blue-900">Cover
                                        Image</label>

                                    {(previewUrl || existingImageUrl) && (
                                        <div className="mt-2">
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Program image"
                                                className="h-full w-full rounded-md object-cover ring-1 ring-gray-200"
                                            />
                                        </div>
                                    )}

                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <input
                                            id="file"
                                            name="file-upload"
                                            type="file"
                                            accept={acceptAttr}
                                            onChange={onPickFile}
                                            className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        {file && (
                                            <button
                                                type="button"
                                                onClick={() => setFile(null)}
                                                className="text-sm text-gray-600 hover:text-blue-900"
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                        {existingImageUrl && !file && (
                                            <button
                                                type="button"
                                                onClick={() => setRemovedExisting(true)}
                                                className="text-sm text-red-600 hover:text-red-500"
                                            >
                                                Remove existing
                                            </button>
                                        )}
                                    </div>

                                    {touched && errors.file &&
                                        <p className="mt-1 text-xs text-red-600">{errors.file}</p>}

                                    {uploadPct > 0 && uploadPct < 100 && (
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100">
                                            <div className="h-full bg-blue-900 transition-all"
                                                 style={{width: `${uploadPct}%`}}/>
                                        </div>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">PNG, JPG up to {MAX_MB}MB.</p>
                                </div>

                                {/* Schedules Editor */}
                                <div>
                                    <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-blue-900">Schedules (by
                                            day-of-week)</label>
                                        <button
                                            type="button"
                                            onClick={addScheduleToList}
                                            className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                                        >
                                            <PlusIcon className="size-4"/> Add
                                        </button>
                                    </div>

                                    <div className="mt-3 space-y-3">
                                        {schedules.map((s, idx) => (
                                            <div key={idx}
                                                 className="grid grid-cols-1 gap-3 sm:grid-cols-12 items-end rounded-md border border-gray-200 p-3">
                                                {/* Day */}
                                                <div className="sm:col-span-3">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">Day</label>
                                                    <div className="relative mt-1">
                                                        <select
                                                            value={s.dayOfWeek}
                                                            onChange={(e) => updateSchedule(idx, {dayOfWeek: e.target.value as DayOfWeek})}
                                                            className="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-2 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                        >
                                                            {DAY_OPTIONS.map(d => <option key={d}
                                                                                          value={d}>{d}</option>)}
                                                        </select>
                                                        <ChevronDownIcon
                                                            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-4 text-gray-500"/>
                                                    </div>
                                                    {touched && (errors[`schedules.${idx}.dayOfWeek`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`schedules.${idx}.dayOfWeek`]}</p>
                                                    )}
                                                </div>

                                                {/* Start */}
                                                <div className="sm:col-span-2">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">Start</label>
                                                    <input
                                                        type="time"
                                                        value={s.startTime}
                                                        onChange={(e) => updateSchedule(idx, {startTime: e.target.value})}
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                    {touched && (errors[`schedules.${idx}.startTime`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`schedules.${idx}.startTime`]}</p>
                                                    )}
                                                </div>

                                                {/* End */}
                                                <div className="sm:col-span-2">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">End</label>
                                                    <input
                                                        type="time"
                                                        value={s.endTime}
                                                        onChange={(e) => updateSchedule(idx, {endTime: e.target.value})}
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                    {touched && (errors[`schedules.${idx}.endTime`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`schedules.${idx}.endTime`]}</p>
                                                    )}
                                                </div>

                                                {/* Producer override */}
                                                <div className="sm:col-span-4">
                                                    <label className="block text-xs font-medium text-blue-900">Producer
                                                        override (optional)</label>
                                                    <input
                                                        type="text"
                                                        value={s.producerOverride ?? ""}
                                                        onChange={(e) => updateSchedule(idx, {producerOverride: e.target.value || null})}
                                                        placeholder="e.g., Weekend Team"
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                </div>

                                                {/* Remove */}
                                                <div className="sm:col-span-1 flex sm:justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSchedule(idx)}
                                                        className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                                                        aria-label="Remove schedule"
                                                    >
                                                        <TrashIcon className="size-6"/>
                                                    </button>
                                                </div>

                                                {/* Range error */}
                                                {touched && (errors[`schedules.${idx}.range`]) && (
                                                    <p className="sm:col-span-12 mt-1 text-xs text-red-600">{errors[`schedules.${idx}.range`]}</p>
                                                )}
                                            </div>
                                        ))}

                                        {schedules.length === 0 && (
                                            <p className="text-xs text-gray-500">No schedules added yet. Click
                                                “Add”.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Overrides Editor */}
                                <div>
                                    <div className="flex items-center justify-between">
                                        <label className="block text-sm font-medium text-blue-900">One-off Overrides
                                            (specific dates)</label>
                                        <button
                                            type="button"
                                            onClick={addOverrideToList}
                                            className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                                        >
                                            <PlusIcon className="size-4"/> Add
                                        </button>
                                    </div>

                                    <div className="mt-3 space-y-3">
                                        {overrides.map((o, idx) => (
                                            <div key={idx}
                                                 className="grid grid-cols-1 gap-3 sm:grid-cols-12 items-end rounded-md border border-gray-200 p-3">
                                                {/* Date */}
                                                <div className="sm:col-span-3">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">Date</label>
                                                    <input
                                                        type="date"
                                                        value={o.date}
                                                        onChange={(e) => updateOverride(idx, {date: e.target.value})}
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                    {touched && (errors[`overrides.${idx}.date`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`overrides.${idx}.date`]}</p>
                                                    )}
                                                </div>

                                                {/* Start */}
                                                <div className="sm:col-span-2">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">Start</label>
                                                    <input
                                                        type="time"
                                                        value={o.startTime}
                                                        onChange={(e) => updateOverride(idx, {startTime: e.target.value})}
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                    {touched && (errors[`overrides.${idx}.startTime`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`overrides.${idx}.startTime`]}</p>
                                                    )}
                                                </div>

                                                {/* End */}
                                                <div className="sm:col-span-2">
                                                    <label
                                                        className="block text-xs font-medium text-blue-900">End</label>
                                                    <input
                                                        type="time"
                                                        value={o.endTime}
                                                        onChange={(e) => updateOverride(idx, {endTime: e.target.value})}
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                    {touched && (errors[`overrides.${idx}.endTime`]) && (
                                                        <p className="mt-1 text-xs text-red-600">{errors[`overrides.${idx}.endTime`]}</p>
                                                    )}
                                                </div>

                                                {/* Producer override */}
                                                <div className="sm:col-span-5">
                                                    <label className="block text-xs font-medium text-blue-900">Producer
                                                        override (optional)</label>
                                                    <input
                                                        type="text"
                                                        value={o.producerOverride ?? ""}
                                                        onChange={(e) => updateOverride(idx, {producerOverride: e.target.value || null})}
                                                        placeholder="Guest Host"
                                                        className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    />
                                                </div>

                                                {/* Optional title/desc/img overrides (advanced) */}
                                                <div className="sm:col-span-12 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-900">Title
                                                            override</label>
                                                        <input
                                                            type="text"
                                                            value={o.titleOverride ?? ""}
                                                            onChange={(e) => updateOverride(idx, {titleOverride: e.target.value || null})}
                                                            placeholder="e.g., Christmas Special"
                                                            className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-900">Description
                                                            override</label>
                                                        <input
                                                            type="text"
                                                            value={o.descriptionOverride ?? ""}
                                                            onChange={(e) => updateOverride(idx, {descriptionOverride: e.target.value || null})}
                                                            className="mt-1 block w-full rounded-md bg-white px-2 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                        />
                                                    </div>

                                                    {/* Image preview for this override */}
                                                    <div className="sm:col-span-3">
                                                        <label className="block text-xs font-medium text-blue-900">Override
                                                            image (optional)</label>

                                                        {(o.previewUrlOverride || o.imageUrl) && (
                                                            <div className="mt-1">
                                                                <img
                                                                    src={o.previewUrlOverride || o.imageUrl!}
                                                                    alt="Override image"
                                                                    className="h-28 w-full rounded-md object-cover ring-1 ring-gray-200"
                                                                />
                                                            </div>
                                                        )}

                                                        <div className="mt-2 flex flex-wrap items-center gap-3">
                                                            <input
                                                                type="file"
                                                                accept={acceptAttr}
                                                                onChange={(e) => onPickOverrideFile(idx, e.target.files?.[0] ?? null)}
                                                                className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                                            />
                                                            {o.imageUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onPickOverrideFile(idx, null)}
                                                                    className="text-sm text-gray-600 hover:text-blue-900"
                                                                >
                                                                    Clear selection
                                                                </button>
                                                            )}
                                                            {o.imageUrl && !o.imageUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateOverride(idx, {file: null})}
                                                                    className="text-sm text-red-600 hover:text-red-500"
                                                                >
                                                                    Remove existing
                                                                </button>
                                                            )}
                                                        </div>

                                                        <p className="mt-1 text-xs text-gray-500">PNG, JPG up
                                                            to {MAX_MB}MB.</p>
                                                    </div>

                                                    {/* Remove */}
                                                    <div className="sm:col-span-1 flex">
                                                        <button
                                                            type="button"
                                                            onClick={() => removeOverride(idx)}
                                                            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                                                            aria-label="Remove schedule"
                                                        >
                                                            <TrashIcon className="size-6"/>
                                                        </button>
                                                    </div>

                                                </div>

                                                {/* Range error */}
                                                {touched && (errors[`overrides.${idx}.range`]) && (
                                                    <p className="sm:col-span-12 mt-1 text-xs text-red-600">{errors[`overrides.${idx}.range`]}</p>
                                                )}
                                            </div>
                                        ))}

                                        {overrides.length === 0 && (
                                            <p className="text-xs text-gray-500">No overrides added yet. Click
                                                “Add”.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="mt-2 flex items-center justify-end gap-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isPending}
                                        className="text-sm font-semibold text-blue-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isPending}
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                    >
                                        {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
